import type { AdminSetting, TestConnectionTarget } from '@ytclip/shared'
import { useMutation } from '@tanstack/react-query'
import { CheckCircle2, PlugZap } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button.tsx'
import { SwitchField, TextField } from '@/components/ui/field.tsx'
import { Badge } from '@/components/ui/feedback.tsx'
import { testConnection } from '@/features/admin/api.ts'

/** A field counts as changed only when it differs from what is stored. */
export function isDirty(setting: AdminSetting, draft: Record<string, string>): boolean {
  const value = draft[setting.key]
  return value !== undefined && value !== setting.value
}

export function SettingControl({
  setting,
  draft,
  onChange,
}: {
  setting: AdminSetting
  draft: Record<string, string>
  onChange: (value: string) => void
}) {
  const value = draft[setting.key] ?? setting.value
  const dirty = isDirty(setting, draft)

  if (setting.type === 'boolean') {
    return (
      <div>
        <SwitchField
          label={setting.label}
          description={setting.description}
          checked={value.toLowerCase() === 'true'}
          onChange={(checked) => onChange(checked ? 'true' : 'false')}
        />
        <FieldMeta setting={setting} dirty={dirty} />
      </div>
    )
  }

  return (
    <div>
      <TextField
        label={setting.label}
        type={setting.secret ? 'password' : setting.type === 'number' ? 'number' : 'text'}
        autoComplete="off"
        placeholder={
          setting.secret
            ? setting.isSet
              ? '•••••••• (tersimpan, isi untuk mengganti)'
              : 'Belum diatur'
            : undefined
        }
        value={value}
        onChange={(event) => onChange(event.target.value)}
        hint={setting.description || undefined}
      />
      <FieldMeta setting={setting} dirty={dirty} />
    </div>
  )
}

function FieldMeta({ setting, dirty }: { setting: AdminSetting; dirty: boolean }) {
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-2">
      {/* Shown first: while a field is edited the source badge describes what is
          still stored, which would otherwise read as contradicting the input. */}
      {dirty ? <Badge tone="warning">Belum disimpan</Badge> : null}
      <SourceBadge setting={setting} />
      {setting.required && !setting.isSet ? <Badge tone="error">Wajib diisi</Badge> : null}
      {setting.updatedBy ? (
        <span className="text-label-sm text-on-surface-variant">
          diubah oleh {setting.updatedBy}
        </span>
      ) : null}
    </div>
  )
}

/** Tells an administrator whether a value is really stored or just a leftover default. */
function SourceBadge({ setting }: { setting: AdminSetting }) {
  if (!setting.isSet) return <Badge tone="neutral">Kosong</Badge>

  if (setting.source === 'database') return <Badge tone="success">Tersimpan di database</Badge>

  if (setting.source === 'environment') return <Badge tone="warning">Dari environment</Badge>

  return <Badge tone="neutral">Nilai bawaan</Badge>
}

export function TestConnectionButton({ target }: { target: TestConnectionTarget }) {
  const [recipient, setRecipient] = useState('')

  const test = useMutation({
    mutationFn: () =>
      testConnection(target, target === 'smtp' ? recipient || undefined : undefined),
    onSuccess: (result) => {
      if (result.ok) toast.success(result.message)
      else toast.error(result.message)
    },
    onError: () => toast.error('Tes koneksi gagal dijalankan'),
  })

  return (
    <div className="flex flex-wrap items-center gap-2">
      {target === 'smtp' ? (
        <input
          type="email"
          value={recipient}
          onChange={(event) => setRecipient(event.target.value)}
          placeholder="email tujuan tes"
          aria-label="Email tujuan tes"
          className="h-10 rounded-full bg-surface-muted px-4 text-label-md text-on-surface outline-none focus-visible:outline-2 focus-visible:outline-primary"
        />
      ) : null}
      <Button
        variant="outlined"
        size="sm"
        loading={test.isPending}
        onClick={() => test.mutate()}
        icon={
          test.isSuccess && test.data?.ok ? (
            <CheckCircle2 className="size-4" />
          ) : (
            <PlugZap className="size-4" />
          )
        }
      >
        Tes koneksi
      </Button>
    </div>
  )
}

export function GroupFields({
  settings,
  target,
  draft,
  onChange,
}: {
  settings: AdminSetting[]
  target?: TestConnectionTarget | undefined
  draft: Record<string, string>
  onChange: (key: string, value: string) => void
}) {
  return (
    <div className="space-y-6 pt-6">
      {target ? (
        <div className="flex justify-end">
          <TestConnectionButton target={target} />
        </div>
      ) : null}

      {settings.map((setting) => (
        <SettingControl
          key={setting.key}
          setting={setting}
          draft={draft}
          onChange={(value) => onChange(setting.key, value)}
        />
      ))}
    </div>
  )
}
